import React, { useEffect, useMemo, useState } from "react";
import {
	Button,
	Col,
	DatePicker,
	Form,
	Input,
	Modal,
	Row,
	Select,
	Space,
	Spin,
	Table,
	Tag,
	Tabs,
	Typography,
	message,
	Checkbox,
} from "antd";
import {
	AntdTable,
	Container,
	ContentPreview,
	DetailBlock,
	DetailHeader,
	DetailItem,
	DetailLabel,
	DetailValue,
	FilterActions,
	FilterContainer,
	FiltersCard,
	FilterLabel,
	FilterSection,
	HeaderBlock,
	Inner,
	SummaryCard,
	SummaryGrid,
	SummaryLabel,
	SummaryValue,
	Subtitle,
	TableCard,
	TabsWrapper,
	Title,
	SummaryCaption,
} from "./styles";
import moment from "moment";
import {
	get_agent_token_usage_conversation_detail,
	get_agent_token_usage_conversations,
	get_agent_token_usage_pointsOfSale,
	get_agent_token_usage_summary,
} from "../../../../api/endpoints/agents";
import PrimaryButton from "../../../common/antd-buttons/PrimaryButton";
import DefaultButton from "../../../common/antd-buttons/DefaultButton";
import { COLORS, Typo } from "../../../layout/theme";
import AntdCheckbox from "../../../common/antd-checkbox/AntdCheckbox";
import { useFilter } from "../../../../contexts/filterContext";
import { AlertOutlined, WarningOutlined } from "@ant-design/icons";

const { RangePicker } = DatePicker;
const { Option } = Select;

const DEFAULT_PAGE_SIZE = 10;

const formatNumber = (value) => Number(value || 0).toLocaleString("es-AR");
const formatDate = (value) =>
	value ? moment(value).format("DD/MM/YYYY HH:mm") : "-";

const getPhaseColor = (phase) => {
	switch (phase) {
		case "Done":
			return "green";
		case "Browsing":
			return "blue";
		case "AwaitingConfirmation":
			return "orange";
		case "Closed":
			return "red";
		default:
			return "default";
	}
};

const normalizeResponse = (response) => ({
	items: response?.items || [],
	paginationInfo: response?.paginationInfo || null,
	pointOfSaleOptions: response?.pointOfSaleOptions || [],
	distributorOptions: response?.distributorOptions || [],
	currentPlanInfo: response?.plan || [],
});

export default function AgentTokenUsagePage({ clientId }) {
	const { clientPlanData } = useFilter();
	const [form] = Form.useForm();

	const [activeTab, setActiveTab] = useState("conversations");

	const [summaryLoading, setSummaryLoading] = useState(false);
	const [conversationsLoading, setConversationsLoading] = useState(false);
	const [posLoading, setPosLoading] = useState(false);
	const [detailLoading, setDetailLoading] = useState(false);

	const [summaryData, setSummaryData] = useState(null);

	const [conversationRows, setConversationRows] = useState([]);
	const [conversationPagination, setConversationPagination] = useState({
		totalRecords: 0,
		totalPages: 0,
		pageSize: DEFAULT_PAGE_SIZE,
		actualPage: 1,
	});

	const [posRows, setPosRows] = useState([]);
	const [posPagination, setPosPagination] = useState({
		totalRecords: 0,
		totalPages: 0,
		pageSize: DEFAULT_PAGE_SIZE,
		actualPage: 1,
	});

	const [pointOfSaleOptions, setPointOfSaleOptions] = useState([]);
	const [distributorOptions, setDistributorOptions] = useState([]);

	const [detailOpen, setDetailOpen] = useState(false);
	const [detailConversationId, setDetailConversationId] = useState(null);
	const [detailRows, setDetailRows] = useState([]);
	const [detailPagination, setDetailPagination] = useState({
		totalRecords: 0,
		totalPages: 0,
		pageSize: 100,
		actualPage: 1,
	});
	const [selectedPointOfSale, setSelectedPointOfSale] = useState(null);
	const [selectedDistributor, setSelectedDistributor] = useState(null);
	const [selectedDateRange, setSelectedDateRange] = useState([
		moment().startOf("month"),
		moment().endOf("month"),
	]);
	const [selectedPhase, setSelectedPhase] = useState(null);
	const [hideEventMessages, setHideEventMessages] = useState(true);
	const [currentPlanInfo, setCurrentPlanInfo] = useState(null);

	const buildCommonFilters = () => {
		const dateFrom = selectedDateRange?.[0]
			? moment(selectedDateRange[0]).format("YYYY-MM-DD")
			: undefined;

		const dateTo = selectedDateRange?.[1]
			? moment(selectedDateRange[1]).format("YYYY-MM-DD")
			: undefined;

		return {
			clientId,
			pointOfSaleId: selectedPointOfSale || undefined,
			distributorId: selectedDistributor || undefined,
			dateFrom,
			dateTo,
			phase: selectedPhase || undefined,
		};
	};

	const loadSummary = async (forcedFilters = null) => {
		try {
			setSummaryLoading(true);

			const params = forcedFilters || {
				...buildCommonFilters(),
				page: 1,
				actualPage: 1,
			};

			const response = await get_agent_token_usage_summary(params);
			const normalized = normalizeResponse(response);

			setSummaryData(normalized.items?.[0] || null);
			setCurrentPlanInfo(normalized?.currentPlanInfo || null);
		} catch (error) {
			message.error("No se pudo obtener el resumen de consumo.");
		} finally {
			setSummaryLoading(false);
		}
	};

	const loadConversations = async (
		page = 1,
		pageSize = conversationPagination.pageSize,
		forcedFilters = null,
	) => {
		try {
			setConversationsLoading(true);

			const params = forcedFilters || {
				...buildCommonFilters(),
				search: form.getFieldValue("search") || undefined,
				sortBy: "Created",
				sortOrder: "DESC",
				page: pageSize,
				actualPage: page,
			};

			const response = await get_agent_token_usage_conversations(params);
			const normalized = normalizeResponse(response);

			setConversationRows(normalized.items || []);
			setConversationPagination(
				normalized.paginationInfo || {
					totalRecords: 0,
					totalPages: 0,
					pageSize,
					actualPage: page,
				},
			);

			setPointOfSaleOptions(normalized.pointOfSaleOptions || []);
			setDistributorOptions(normalized.distributorOptions || []);
		} catch (error) {
			message.error("No se pudo obtener el consumo por conversación.");
		} finally {
			setConversationsLoading(false);
		}
	};

	const loadPointsOfSale = async (
		page = 1,
		pageSize = posPagination.pageSize,
		forcedFilters = null,
	) => {
		try {
			setPosLoading(true);

			const params = forcedFilters || {
				...buildCommonFilters(),
				search: form.getFieldValue("search") || undefined,
				sortBy: "TotalTokens",
				sortOrder: "DESC",
				page: pageSize,
				actualPage: page,
			};

			const response = await get_agent_token_usage_pointsOfSale(params);
			const normalized = normalizeResponse(response);

			setPosRows(normalized.items || []);
			setPosPagination(
				normalized.paginationInfo || {
					totalRecords: 0,
					totalPages: 0,
					pageSize,
					actualPage: page,
				},
			);
		} catch (error) {
			message.error("No se pudo obtener el consumo por punto de venta.");
		} finally {
			setPosLoading(false);
		}
	};

	const loadConversationDetail = async (
		conversationId,
		page = 1,
		pageSize = 100,
	) => {
		try {
			setDetailLoading(true);

			const response = await get_agent_token_usage_conversation_detail({
				conversationId,
				page: pageSize,
				actualPage: page,
			});

			const normalized = normalizeResponse(response);

			setDetailRows(normalized.items || []);
			setDetailPagination(
				normalized.paginationInfo || {
					totalRecords: 0,
					totalPages: 0,
					pageSize,
					actualPage: page,
				},
			);
		} catch (error) {
			message.error("No se pudo obtener el detalle de la conversación.");
		} finally {
			setDetailLoading(false);
		}
	};

	const handleSearch = async () => {
		await loadSummary();
		await loadConversations(
			1,
			conversationPagination.pageSize || DEFAULT_PAGE_SIZE,
		);
		await loadPointsOfSale(1, posPagination.pageSize || DEFAULT_PAGE_SIZE);
	};

	const getEmptyFilters = () => ({
		clientId,
		pointOfSaleId: undefined,
		distributorId: undefined,
		dateFrom: moment().startOf("month").format("YYYY-MM-DD"),
		dateTo: moment().endOf("month").format("YYYY-MM-DD"),
		phase: undefined,
		search: undefined,
	});

	const handleReset = async () => {
		form.resetFields();

		setSelectedPointOfSale(null);
		setSelectedDistributor(null);
		const defaultMonthRange = [
			moment().startOf("month"),
			moment().endOf("month"),
		];
		setSelectedDateRange(defaultMonthRange);
		setSelectedPhase(null);
		setCurrentPlanInfo(null);

		const emptyFilters = {
			...getEmptyFilters(),
			page: 1,
			actualPage: 1,
		};

		await loadSummary(emptyFilters);
		await loadConversations(1, DEFAULT_PAGE_SIZE, {
			...emptyFilters,
			sortBy: "Created",
			sortOrder: "DESC",
			page: DEFAULT_PAGE_SIZE,
			actualPage: 1,
		});
		await loadPointsOfSale(1, DEFAULT_PAGE_SIZE, {
			...emptyFilters,
			sortBy: "TotalTokens",
			sortOrder: "DESC",
			page: DEFAULT_PAGE_SIZE,
			actualPage: 1,
		});
	};

	const handleDateChange = (dates) => {
		setSelectedDateRange(dates || null);
		setSelectedPointOfSale(null);
		setSelectedDistributor(null);

		form.setFieldsValue({
			pointOfSaleId: undefined,
			distributorId: undefined,
		});
	};

	const openDetail = async (conversationId) => {
		setDetailConversationId(conversationId);
		setDetailOpen(true);
		await loadConversationDetail(conversationId, 1, 100);
	};

	useEffect(() => {
		handleSearch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clientId]);

	const conversationColumns = [
		{
			title: "Punto de venta",
			dataIndex: "pointOfSaleName",
			key: "pointOfSaleName",
			render: (value) => value || "-",
		},
		{
			title: "Drogueria",
			dataIndex: "distributorName",
			key: "distributorName",
			render: (value) => value || "-",
		},
		{
			title: "Fase",
			dataIndex: "phase",
			key: "phase",
			render: (value) => <Tag color={getPhaseColor(value)}>{value || "-"}</Tag>,
		},
		{
			title: "Mensajes usuario",
			dataIndex: "userMessageCount",
			key: "userMessageCount",
			render: (value) => formatNumber(value),
		},
		{
			title: "Total conversación",
			dataIndex: "totalTokensUsed",
			key: "totalTokensUsed",
			render: (value) => (
				<Typo level={6} type="primary" weight="ExtraBold">
					{formatNumber(value)}
				</Typo>
			),
		},
		{
			title: "Creada",
			dataIndex: "created",
			key: "created",
			render: (value) => formatDate(value),
		},
		{
			title: "Último mensaje",
			dataIndex: "lastUserMessageAt",
			key: "lastUserMessageAt",
			render: (value) => formatDate(value),
		},
		{
			title: "Acciones",
			key: "actions",
			// fixed: "right",
			render: (_, record) => (
				<Button type="link" onClick={() => openDetail(record.conversationId)}>
					Ver detalle
				</Button>
			),
		},
	];

	const posColumns = [
		{
			title: "Punto de venta",
			dataIndex: "pointOfSaleName",
			key: "pointOfSaleName",
			render: (value) => value || "-",
		},
		{
			title: "Conversaciones",
			dataIndex: "conversationsCount",
			key: "conversationsCount",
			align: "right",
			render: (value) => formatNumber(value),
		},
		{
			title: "Total tokens",
			dataIndex: "totalTokens",
			key: "totalTokens",
			align: "right",
			render: (value) => (
				<Typo level={6} type="primary" weight="ExtraBold">
					{formatNumber(value)}
				</Typo>
			),
		},
		{
			title: "Promedio por conv.",
			dataIndex: "avgTokensPerConversation",
			key: "avgTokensPerConversation",
			align: "right",
			render: (value) => formatNumber(value),
		},
		{
			title: "Última conversación",
			dataIndex: "lastConversationAt",
			key: "lastConversationAt",
			render: (value) => formatDate(value),
		},
	];

	const detailColumns = [
		{
			title: "Fecha",
			dataIndex: "created",
			key: "created",
			width: 150,
			render: (value) => formatDate(value),
		},
		{
			title: "Rol",
			dataIndex: "role",
			key: "role",
			width: 100,
			render: (value) => {
				const color =
					value === "assistant"
						? "blue"
						: value === "user"
							? "green"
							: "default";
				return <Tag color={color}>{value || "-"}</Tag>;
			},
		},
		{
			title: "Evento",
			dataIndex: "isEvent",
			key: "isEvent",
			width: 90,
			render: (value) => (value ? <Tag>Sí</Tag> : <Tag color="purple">No</Tag>),
		},
		{
			title: "Total",
			dataIndex: "totalTokens",
			key: "totalTokens",
			width: 120,
			align: "right",
			render: (value) => formatNumber(value),
		},
		{
			title: "Contenido",
			dataIndex: "content",
			key: "content",
			render: (value) => <ContentPreview>{value || "-"}</ContentPreview>,
		},
	];

	const visibleDetailRows = useMemo(() => {
		if (!hideEventMessages) return detailRows || [];
		return (detailRows || []).filter((row) => !row.isEvent);
	}, [detailRows, hideEventMessages]);

	const hasCurrentPlan =
		!!currentPlanInfo &&
		(currentPlanInfo?.iaPlanId ||
			currentPlanInfo?.IAPlanId ||
			currentPlanInfo?.planTitle ||
			currentPlanInfo?.PlanTitle);

	return (
		<Inner>
			<Container>
				<HeaderBlock>
					<Title>Consumo de tokens del agente</Title>
					<Subtitle>
						Resumen general, consumo por conversación y consumo por punto de
						venta.
					</Subtitle>
				</HeaderBlock>

				<FiltersCard>
					<Form form={form} layout="vertical">
						<FilterContainer>
							<FilterSection>
								<FilterLabel>Rango de fechas</FilterLabel>
								<RangePicker
									style={{ width: "100%" }}
									format="DD/MM/YYYY"
									value={selectedDateRange}
									onChange={handleDateChange}
								/>
							</FilterSection>

							<FilterSection>
								<FilterLabel>Punto de venta</FilterLabel>
								<Select
									showSearch
									allowClear
									placeholder="Seleccionar punto de venta"
									options={pointOfSaleOptions}
									optionFilterProp="label"
									filterOption={(input, option) =>
										(option?.label ?? "")
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									value={selectedPointOfSale}
									onChange={(value) => setSelectedPointOfSale(value || null)}
								/>
							</FilterSection>

							<FilterSection>
								<FilterLabel>Drogueria</FilterLabel>
								<Select
									showSearch
									allowClear
									placeholder="Seleccionar drogueria"
									options={distributorOptions}
									optionFilterProp="label"
									filterOption={(input, option) =>
										(option?.label ?? "")
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									value={selectedDistributor}
									onChange={(value) => setSelectedDistributor(value || null)}
								/>
							</FilterSection>

							<FilterSection>
								<FilterLabel>Fase</FilterLabel>
								<Select
									allowClear
									placeholder="Seleccionar fase"
									value={selectedPhase}
									onChange={(value) => setSelectedPhase(value || null)}
								>
									<Option value="Idle">Inactiva</Option>
									<Option value="AddingItems">Agregando productos</Option>
									<Option value="ReviewingCart">Revisando carro</Option>
									<Option value="SelectingDistributor">
										Eligiendo drogueria
									</Option>
									<Option value="ConfirmingDistributor">
										Confirmando drogueria
									</Option>
									<Option value="ConfirmingCode">
										Confirmando codigo cliente
									</Option>
									<Option value="AwaitingFinalConfirmation">
										Esperando confirmacion
									</Option>
									<Option value="Done">Terminada</Option>
								</Select>
							</FilterSection>

							<FilterActions>
								<PrimaryButton type="primary" onClick={handleSearch}>
									Aplicar filtros
								</PrimaryButton>
								<DefaultButton
									onClick={handleReset}
									style={{
										border: `1px solid ${COLORS.MediumGrey}`,
									}}
								>
									Limpiar
								</DefaultButton>
							</FilterActions>
						</FilterContainer>
					</Form>
				</FiltersCard>

				<Spin spinning={summaryLoading}>
					<SummaryGrid columns={hasCurrentPlan ? 5 : 4}>
						<SummaryCard className="highlight">
							<SummaryLabel>Total consumido</SummaryLabel>
							<SummaryValue>
								{formatNumber(summaryData?.totalTokensUsed)}
							</SummaryValue>
							<SummaryCaption>Tokens totales de conversaciones</SummaryCaption>
						</SummaryCard>

						<SummaryCard>
							<SummaryLabel>Conversaciones</SummaryLabel>
							<SummaryValue>
								{formatNumber(summaryData?.totalConversations)}
							</SummaryValue>
							<SummaryCaption>Cantidad total en el período</SummaryCaption>
						</SummaryCard>

						<SummaryCard>
							<SummaryLabel>Puntos de venta</SummaryLabel>
							<SummaryValue>
								{formatNumber(summaryData?.totalPointOfSales)}
							</SummaryValue>
							<SummaryCaption>PDV con actividad en el rango</SummaryCaption>
						</SummaryCard>

						{hasCurrentPlan ? (
							<>
								<SummaryCard
									borderColor={
										clientPlanData
											? clientPlanData.usagePercent > 60 &&
												clientPlanData.usagePercent < 80 &&
												!clientPlanData.shouldBlock
												? COLORS.Warning
												: clientPlanData.usagePercent > 80 &&
													  !clientPlanData.shouldBlock
													? COLORS.Danger
													: clientPlanData.shouldBlock
														? COLORS.Danger
														: null
											: null
									}
								>
									<SummaryLabel>
										Tokens restantes (
										{currentPlanInfo?.currentMonthName ?? "mes actual"})
									</SummaryLabel>
									<SummaryValue>
										{formatNumber(currentPlanInfo?.remainingTokens)}
									</SummaryValue>
									<SummaryCaption
										textColor={
											clientPlanData
												? clientPlanData.usagePercent > 60 &&
													clientPlanData.usagePercent < 80 &&
													!clientPlanData.shouldBlock
													? COLORS.Warning
													: clientPlanData.usagePercent > 80 &&
														  !clientPlanData.shouldBlock
														? COLORS.Danger
														: clientPlanData.shouldBlock
															? COLORS.Danger
															: null
												: null
										}
									>
										{clientPlanData
											? clientPlanData.usagePercent > 60 && <WarningOutlined />
											: null}{" "}
										{currentPlanInfo?.remainingPercent || 0}% disponible
										{clientPlanData &&
											clientPlanData.shouldBlock &&
											". Servicio pausado."}
									</SummaryCaption>
								</SummaryCard>

								<SummaryCard>
									<SummaryLabel>
										Consumo actual (
										{currentPlanInfo?.currentMonthName ?? "mes actual"})
									</SummaryLabel>
									<SummaryValue>
										~{" "}
										{formatNumber(
											(
												(currentPlanInfo?.currentMonthTokensUsed || 0) *
												(currentPlanInfo?.tokenPrice || 0)
											).toFixed(1),
										)}{" "}
										USD
									</SummaryValue>
									<SummaryCaption>
										{formatNumber((currentPlanInfo?.tokenPrice || 0) * 10000)}{" "}
										USD valor por 10k tokens
									</SummaryCaption>
								</SummaryCard>
							</>
						) : (
							<SummaryCard>
								<SummaryLabel>Promedio por conversación</SummaryLabel>
								<SummaryValue>
									{formatNumber(summaryData?.avgTokensPerConversation)}
								</SummaryValue>
								<SummaryCaption>
									Consumo promedio por interacción
								</SummaryCaption>
							</SummaryCard>
						)}
					</SummaryGrid>
				</Spin>

				<TableCard>
					<TabsWrapper>
						<Tabs
							activeKey={activeTab}
							onChange={setActiveTab}
							items={[
								{
									key: "conversations",
									label: "Por conversación",
									children: (
										<AntdTable
											rowKey="conversationId"
											columns={conversationColumns}
											dataSource={conversationRows}
											loading={conversationsLoading}
											// scroll={{ x: 1700 }}
											pagination={{
												current: conversationPagination.actualPage,
												pageSize: conversationPagination.pageSize,
												total: conversationPagination.totalRecords,
												showSizeChanger: true,
												showTotal: (total) => `${total} registros`,
												onChange: (page, pageSize) =>
													loadConversations(page, pageSize),
											}}
										/>
									),
								},
								{
									key: "points-of-sale",
									label: "Por punto de venta",
									children: (
										<AntdTable
											rowKey="pointOfSaleId"
											columns={posColumns}
											dataSource={posRows}
											loading={posLoading}
											scroll={{ x: 1200 }}
											pagination={{
												current: posPagination.actualPage,
												pageSize: posPagination.pageSize,
												total: posPagination.totalRecords,
												showSizeChanger: true,
												showTotal: (total) => `${total} registros`,
												onChange: (page, pageSize) =>
													loadPointsOfSale(page, pageSize),
											}}
										/>
									),
								},
							]}
						/>
					</TabsWrapper>
				</TableCard>

				<Modal
					title="Detalle de conversación"
					open={detailOpen}
					onCancel={() => {
						setDetailOpen(false);
						setDetailRows([]);
						setDetailConversationId(null);
					}}
					footer={null}
					width={1200}
				>
					<DetailBlock>
						<DetailHeader>
							<DetailItem>
								<DetailLabel>Mensajes cargados</DetailLabel>
								<DetailValue>
									{formatNumber(visibleDetailRows?.length)}
								</DetailValue>{" "}
							</DetailItem>

							<DetailItem>
								<DetailLabel>Total registros</DetailLabel>
								<DetailValue>
									{formatNumber(detailPagination?.totalRecords)}
								</DetailValue>
							</DetailItem>
							<DetailItem
								style={{ alignItems: "center", justifyContent: "center" }}
							>
								<AntdCheckbox
									checked={hideEventMessages}
									onChange={(e) => setHideEventMessages(e.target.checked)}
								>
									Ocultar mensajes de evento
								</AntdCheckbox>
							</DetailItem>
						</DetailHeader>

						<AntdTable
							rowKey="messageId"
							columns={detailColumns}
							dataSource={visibleDetailRows}
							loading={detailLoading}
							pagination={{
								current: detailPagination.actualPage,
								pageSize: detailPagination.pageSize,
								total: hideEventMessages
									? visibleDetailRows.length
									: detailPagination.totalRecords,
								showSizeChanger: true,
								showTotal: (total) => `${total} mensajes`,
								onChange: (page, pageSize) =>
									loadConversationDetail(detailConversationId, page, pageSize),
							}}
						/>
					</DetailBlock>
				</Modal>
			</Container>
		</Inner>
	);
}
